import React, { useState, useEffect } from 'react';
import './castingCall.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Modal from '@mui/material/Modal';
import {
  TextField,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';

const RoleEditModal = ({
  openRoleEditModal,
  setOpenRoleEditModal,
  handleEditRole,
  selectedEditRole,
  setSelectedEditRole,
}) => {
  const ethnicityOptions = [
    'FBA',
    'African American',
    'Middle Eastern',
    'Latino Hispanic',
    'Native American',
    'European American',
    'Pacific Islander',
    'Others',
    'Indian South Asian',
    'Asian',
  ];
  const auditionTypeOptions = [
    'Open Casting Call',
    'Closed Casting Call',
    'Casting by Self-Tape',
    'Extras Casting Call',
    'Commercial Casting Call',
    'Theatrical Casting Call',
    'Film/TV Casting Call',
  ];
  const initialValue = {
    title: '',
    description: '',
    gender: '',
    ageMin: '',
    ageMax: '',
    ethinicity: [],
    castingRole: 'actor',
    auditionType: '',
  };
  const [formData, setFormData] = useState(initialValue);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedEditRole) {
      setFormData({
        title: selectedEditRole?.title || '',
        description: selectedEditRole?.description || '',
        gender: selectedEditRole?.gender || '',
        ageMin: selectedEditRole?.ageMin || '',
        ageMax: selectedEditRole?.ageMax || '',
        ethinicity: selectedEditRole?.ethinicity || '',
        castingRole: selectedEditRole?.castingRole || '',
        auditionType: selectedEditRole?.auditionType || '',
      });
    }
  }, [openRoleEditModal, setOpenRoleEditModal, selectedEditRole]);

  //close modal
  const handleClose = () => {
    setOpenRoleEditModal(!openRoleEditModal);
    setErrors({});
    setFormData(initialValue);
  };

  //save changed inputs
  const handleInputChange = (name, value) => {
    if (name === 'ethinicity') {
      setFormData((prevDetails) => {
        const newEthnicities = prevDetails.ethinicity?.includes(value)
          ? prevDetails.ethinicity?.filter((ethnicity) => ethnicity !== value)
          : [...prevDetails.ethinicity, value];

        return {
          ...prevDetails,
          ethinicity: newEthnicities,
        };
      });
    } else {
      setFormData((prevDetails) => ({
        ...prevDetails,
        [name]: value,
      }));
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  //validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.title) {
      errors.title = 'Please add role title';
    }
    if (!formData.description) {
      errors.description = 'Please add small description';
    }
    if (!formData.gender) {
      errors.gender = 'Please select gender';
    }
    if (!formData.auditionType) {
      errors.auditionType = 'Please select audition type';
    }
    if (!formData.ageMin) {
      errors.ageMin = 'Please add min age';
    }
    if (!formData.ageMax) {
      errors.ageMax = 'Please add max age';
    }
    if (formData.ageMin && formData.ageMax) {
      const ageMin = Number(formData.ageMin);
      const ageMax = Number(formData.ageMax);
      if (ageMax < ageMin) {
        errors.ageMax = 'The maximum age must be greater than the minimum age';
      }
    }
    if (formData.ethinicity?.length === 0) {
      errors.ethinicity = 'Please choose at least one ethnicity';
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  //add a role
  const handleSave = () => {
    if (validateForm()) {
      handleEditRole(formData);
      handleClose();
      setSelectedEditRole('');
      setFormData(initialValue);
    }
  };

  return (
    <Modal
      open={openRoleEditModal}
      onClose={handleClose}
      sx={{ overflowY: 'auto' }}
    >
      <div className='role-add-modal-wrapper'>
        <div className='role-add-modal-container'>
          <div className='role-add-modal-title'>
            <span className='all-role-count'>Edit role</span>
            <div className='modal-close-icon' onClick={handleClose}>
              <FontAwesomeIcon icon={faTimes} />{' '}
            </div>
          </div>
          <TextField
            label='Role Title'
            name='title'
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            variant='filled'
            fullWidth
            inputProps={{ style: { textTransform: 'uppercase' } }}
            sx={{
              backgroundColor: 'transparent',
              '& .MuiFilledInput-root': {
                backgroundColor: 'transparent',
              },
            }}
            error={!!errors.title}
            helperText={errors.title}
          />
          <TextField
            label='Write a short description'
            name='description'
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            variant='filled'
            fullWidth
            sx={{
              backgroundColor: 'transparent',
              '& .MuiFilledInput-root': {
                backgroundColor: 'transparent',
              },
              mt: '10px',
            }}
            error={!!errors.description}
            helperText={errors.description}
          />
          <FormControl
            fullWidth
            variant='filled'
            sx={{
              backgroundColor: 'transparent',
              '& .MuiFilledInput-root': {
                backgroundColor: 'transparent',
              },
              mt: '10px',
            }}
          >
            <InputLabel>Gender</InputLabel>
            <Select
              value={formData.gender}
              onChange={(e) => handleInputChange('gender', e.target.value)}
              name='gender'
              MenuProps={{
                disableScrollLock: true,
              }}
            >
              <MenuItem color='#6e0506' value='male'>
                Male
              </MenuItem>
              <MenuItem color='#6e0506' value='female'>
                Female
              </MenuItem>
              <MenuItem color='#6e0506' value='non-binary'>
                Non-Binary
              </MenuItem>
              <MenuItem color='#6e0506' value='transgender'>
                Transgender
              </MenuItem>
            </Select>
            {errors.gender && (
              <Typography variant='caption' color='error' sx={{ ml: '15px' }}>
                {errors.gender}
              </Typography>
            )}
          </FormControl>

          <FormControl
            fullWidth
            variant='filled'
            sx={{
              backgroundColor: 'transparent',
              '& .MuiFilledInput-root': {
                backgroundColor: 'transparent',
              },
              mt: '10px',
            }}
          >
            <InputLabel>Audition Type</InputLabel>
            <Select
              value={formData.auditionType}
              onChange={(e) =>
                handleInputChange('auditionType', e.target.value)
              }
              name='auditionType'
              sx={{ color: '#6e0506' }}
              MenuProps={{
                disableScrollLock: true,
                color: '#6e0506',
              }}
            >
              {auditionTypeOptions.map((type) => (
                <MenuItem key={type} color='#6e0506' value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {errors.auditionType && (
              <Typography variant='caption' color='error' sx={{ ml: '15px' }}>
                {errors.auditionType}
              </Typography>
            )}
          </FormControl>

          <div className='grid-inputs'>
            <TextField
              label='Min Age'
              name='ageMin'
              type='number'
              value={formData.ageMin}
              onChange={(e) => handleInputChange('ageMin', e.target.value)}
              variant='filled'
              fullWidth
              sx={{
                backgroundColor: 'transparent',
                '& .MuiFilledInput-root': {
                  backgroundColor: 'transparent',
                },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                  {
                    display: 'none',
                  },
                mt: '10px',
              }}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                  e.preventDefault();
                }
              }}
              error={!!errors.ageMin}
              helperText={errors.ageMin}
            />

            <TextField
              label='Max Age'
              name='ageMax'
              type='number'
              value={formData.ageMax}
              onChange={(e) => handleInputChange('ageMax', e.target.value)}
              variant='filled'
              fullWidth
              sx={{
                backgroundColor: 'transparent',
                '& .MuiFilledInput-root': {
                  backgroundColor: 'transparent',
                },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                  {
                    display: 'none',
                  },
                mt: '10px',
              }}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                  e.preventDefault();
                }
              }}
              error={!!errors.ageMax}
              helperText={errors.ageMax}
            />
          </div>
          <div className='ethnicity-topic'>
            <span className='castingCall-create-section-topic'>
              Ethnicities(choose at least one)
            </span>
            {errors.ethinicity && (
              <Typography variant='caption' color='error'>
                {errors.ethinicity}
              </Typography>
            )}
          </div>
          <div className='checkbox-inputs'>
            {ethnicityOptions.map((ethinicityItem) => (
              <FormControlLabel
                key={ethinicityItem}
                control={
                  <Checkbox
                    checked={formData.ethinicity?.includes(ethinicityItem)}
                    onChange={(e) =>
                      handleInputChange('ethinicity', ethinicityItem)
                    }
                    inputProps={{ 'aria-label': ethinicityItem }}
                  />
                }
                label={ethinicityItem}
                sx={{ color: 'gray' }}
              />
            ))}
          </div>
          <div>
            <button className='review-submit-button' onClick={handleSave}>
              SAVE
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RoleEditModal;
